import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ControlledInput,
  ControlledPhoneInput,
  ControlledSelect
} from '@@viewCommonComponents/controlled';
import { DashedTitle, InfoMessage } from '@fasstech/middle-design-system';
import { FormLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import {
  emailRules,
  emailIsValid,
  isLandlinePhoneNumberValid,
  isPhoneNumberValid,
  isRequired,
  isRequiredSelect
} from '@@viewHelpers/validations/index.js';
import { propOr } from 'ramda';
import useGetSimulationPavillonInformationCitiesQuery from '../hooks/useGetSimulationPavillonInformationCitiesQuery.js';

const CustomerForm = ({ selectedTab, phoneIndicatives }) => {
  const form = useFormContext();

  const postalCode = form.watch('customer.addresses.0.zipCode');
  const mobile = form.watch('customer.phones.0.number');
  const landline = form.watch('customer.phones.1.number');
  const email = form.watch('customer.emails.0.value');
  const prevMobile = useRef(mobile);
  const prevLandline = useRef(landline);

  const { getSimulationPavillonInformationCities: cities } =
    useGetSimulationPavillonInformationCitiesQuery({
      variables: {
        postalCode
      }
    });

  const mobileValid =
    mobile && isPhoneNumberValid(true).validate(mobile) === true;
  const landlineValid =
    landline && isLandlinePhoneNumberValid(true).validate(landline) === true;
  const emailValid = email && emailIsValid(email) === true;
  const hasAtLeastOneValid = mobileValid || landlineValid || emailValid;

  useEffect(() => {
    form.trigger([
      'customer.phones.0.number',
      'customer.phones.1.number',
      'customer.emails.0.value'
    ]);
  }, [mobile, landline, email]);

  useEffect(() => {
    if (prevMobile.current !== mobile) {
      prevMobile.current = mobile;
      const timer = setTimeout(() => {
        form.trigger('customer.phones.0.internationalCode');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [mobile]);

  useEffect(() => {
    if (prevLandline.current !== landline) {
      prevLandline.current = landline;
      const timer = setTimeout(() => {
        form.trigger('customer.phones.1.internationalCode');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [landline]);

  if (selectedTab !== 0) {
    return null;
  }

  return (
    <FormLayout>
      <DashedTitle>Adhérent</DashedTitle>

      <ControlledInput
        name="customer.addresses.0.fields.0"
        label={'Adresse'}
        form={form}
        rules={isRequired}
      />

      <ControlledInput
        name="customer.addresses.0.fields.1"
        label={'Appartement, Etage'}
        form={form}
      />

      <ControlledInput
        name="customer.addresses.0.fields.2"
        label={'Complément d’adresse (Société, Bâtiment, …)'}
        form={form}
      />

      <ControlledInput
        name="customer.addresses.0.zipCode"
        label={'Code postal'}
        form={form}
        rules={isRequired}
        disabled={true}
      />

      <ControlledSelect
        name="customer.addresses.0.city"
        label={'Ville'}
        form={form}
        options={propOr([], 'cities', cities)}
        rules={isRequiredSelect(propOr([], 'cities', cities))}
      />

      <ControlledPhoneInput
        name="customer.phones.0"
        label={'N° tel mobile'}
        form={form}
        rules={{
          required: !hasAtLeastOneValid,
          ...isPhoneNumberValid(!hasAtLeastOneValid)
        }}
        isInternational
        selectRules={{}}
        options={phoneIndicatives}
      />

      <InfoMessage iconName={'lightbulb'}>
        {'Numéro de mobile nécessaire pour la signature électronique'}
      </InfoMessage>

      <ControlledPhoneInput
        name="customer.phones.1"
        isMobile={false}
        label={'N° tel fixe'}
        form={form}
        rules={{
          required: !hasAtLeastOneValid,
          ...isLandlinePhoneNumberValid(!hasAtLeastOneValid)
        }}
      />

      <ControlledInput
        name="customer.emails.0.value"
        label={'Email'}
        form={form}
        rules={{
          required: !hasAtLeastOneValid,
          ...emailRules(!hasAtLeastOneValid)
        }}
      />

      <InfoMessage iconName={'lightbulb'}>
        {'Email nécessaire pour la signature électronique et la création de l’espace adhérent'}
      </InfoMessage>
    </FormLayout>
  );
};

export default CustomerForm;
